import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { CodingModel, DiagnosisAuthorType, DiagnosisRole, GuaranteeDecision, GuaranteesV2Service, PrescriptionWriteModel } from '@lib/guarantees/data-access';
import {
  DECISION_ICON,
  DECISION_LABEL_KEY,
  DecisionDraft,
  DiagnosisChipComponent,
  DiagnosisSummary,
  Icd10PickerComponent,
  PRESCRIPTION_FORM_DIALOG_CONFIG,
  PrescriptionFormComponent,
  PrescriptionFormData,
  PrescriptionFormResult,
  SERVICE_KIND_ICON,
  SERVICE_KIND_LABEL_KEY,
  toDecisionWriteModel,
} from '@lib/guarantees/feature';
import { MemberHubService } from '@lib/members/data-access';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { catchError, map, of, take } from 'rxjs';

import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';

import { formatDate } from '@lib/commons/util-functions/format-date.function';

import { LoaderComponent } from '@lib/shared-ui/layout/loader/loader.component';

import { TaskDataTargetComponent } from '../../../components/commons/task-data-target/task-data-target.component';
import { TASK_DATA } from '../../../core/contracts/task-data.contract';
import { Task, TaskStatus, TaskType } from '../../../core/models/task.interface';
import * as TaskActions from '../../../core/store/task.actions';
import { MultilineTextComponent } from '../../commons/multiline-text/multiline-text.component';
import { MEMBER_COVERAGE_DIALOG_CONFIG, MemberCoverageDialogComponent, MemberCoverageDialogData } from './member-coverage-dialog/member-coverage-dialog.component';

/** The one line a service request raises, held with its ruling until the record is written. */
interface QueuedLine {
  prescription: PrescriptionWriteModel;
  decision?: DecisionDraft;
}

@Component({
  selector: 'tasks-service-request-task-details',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatIcon,
    MatTooltip,
    TranslatePipe,
    Icd10PickerComponent,
    DiagnosisChipComponent,
    MultilineTextComponent,
    TaskDataTargetComponent,
    LoaderComponent,
  ],
  templateUrl: './service-request-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col gap-6' },
})
export class ServiceRequestTaskDetailsComponent {
  private readonly router = inject(Router);
  private readonly dialog = inject(MatDialog);
  private readonly store = inject(Store);
  private readonly translate = inject(TranslateService);
  private readonly memberHubService = inject(MemberHubService);
  private readonly guaranteesService = inject(GuaranteesV2Service);

  protected readonly formatDate = formatDate;
  protected readonly taskStatus = TaskStatus;
  protected readonly kindIcon = SERVICE_KIND_ICON;
  protected readonly kindLabelKey = SERVICE_KIND_LABEL_KEY;
  protected readonly decisionIcon = DECISION_ICON;
  protected readonly decisionLabelKey = DECISION_LABEL_KEY;

  protected readonly task = inject<Task<TaskType.ServiceRequest>>(TASK_DATA);
  protected readonly memberUserId = toSignal(
    this.memberHubService.getMemberProfile(this.task.data.memberId).pipe(
      map(profile => profile?.userId ?? null),
      catchError(() => of(null))
    )
  );

  protected readonly saving = signal<boolean>(false);
  protected readonly saveError = signal<string | null>(null);

  protected readonly diagnosis = signal<CodingModel | null>(null);
  protected readonly line = signal<QueuedLine | null>(null);
  protected readonly pickerKey = signal(0);
  protected readonly descriptionFc = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });

  protected readonly diagnosisSummary = computed<DiagnosisSummary | null>(() => {
    const coding = this.diagnosis();
    return coding ? { role: DiagnosisRole.Primary, icd10: coding, authorType: DiagnosisAuthorType.HealthManager } : null;
  });

  protected readonly canSubmit = computed(() => !!this.diagnosis() && !!this.line()?.decision && !this.saving());

  private readonly lineDescriptionEffect = effect(() => {
    const line = this.line();
    this.descriptionFc.setValue(line ? this.buildDescription(line.prescription.name || line.prescription.code, this.task.data.notes) : '');
  });

  private buildDescription(name: string, notes: string): string {
    const lines = notes?.split('\n') ?? [];
    const answers = lines.filter((_, i) => i % 2 === 1);
    const base = this.translate.instant('prescription.message.serviceRequestDescription', { name });
    return answers.length ? `${base}. ${answers.join(' -> ')}` : base;
  }

  protected addDiagnosis(coding: CodingModel): void {
    this.diagnosis.set(coding);
    this.pickerKey.update(key => key + 1);
    this.line.update(current => (current ? { ...current, prescription: { ...current.prescription, diagnosisId: coding.code } } : current));
  }

  /** A grant cannot stand on a line with no diagnosis behind it, so it goes with the code. */
  protected removeDiagnosis(): void {
    this.diagnosis.set(null);
    this.pickerKey.update(key => key + 1);
    this.line.update(current => {
      if (!current) return current;
      const decision = current.decision?.answer === GuaranteeDecision.Guaranteed ? undefined : current.decision;
      return { prescription: { ...current.prescription, diagnosisId: undefined }, decision };
    });
  }

  protected openLineForm(): void {
    const memberUserId = this.memberUserId();
    if (memberUserId === undefined) return;

    const queued = this.line();
    const coding = this.diagnosis();
    const data: PrescriptionFormData = {
      mode: queued ? 'draft' : 'create',
      initial: queued?.prescription,
      diagnoses: coding ? [{ id: coding.code ?? '', role: DiagnosisRole.Primary, icd10: coding }] : [],
      collectsDecision: true,
      decision: queued?.decision,
      memberUserId,
    };

    this.dialog
      .open<PrescriptionFormComponent, PrescriptionFormData, PrescriptionFormResult>(PrescriptionFormComponent, { ...PRESCRIPTION_FORM_DIALOG_CONFIG, data })
      .afterClosed()
      .pipe(take(1))
      .subscribe(result => {
        if (result) this.line.set({ prescription: result.prescription, decision: result.decision });
      });
  }

  protected removeLine(): void {
    this.line.set(null);
  }

  protected submit(): void {
    const coding = this.diagnosis();
    const queued = this.line();
    if (!coding || !queued?.decision || this.descriptionFc.invalid || this.saving()) return;

    const diagnosisId = crypto.randomUUID();

    this.saving.set(true);
    this.saveError.set(null);

    this.guaranteesService
      .createGuarantee(this.task.data.memberId, {
        isServiceRequest: true,
        isLiloStaff: false,
        description: this.descriptionFc.value.trim(),
        diagnoses: [{ id: diagnosisId, role: DiagnosisRole.Primary, icd10Code: coding.code, authorType: DiagnosisAuthorType.HealthManager }],
        prescriptions: [
          {
            ...queued.prescription,
            diagnosisId,
            decision: toDecisionWriteModel(queued.decision),
            coverage: queued.decision.answer === GuaranteeDecision.Guaranteed ? queued.prescription.coverage : undefined,
          },
        ],
        markProcessed: true,
      })
      .pipe(take(1))
      .subscribe({
        next: () => this.onGuaranteeCreated(),
        error: err => {
          this.saving.set(false);
          this.saveError.set(err?.error?.message || 'guaranteeV2.message.writeError');
        },
      });
  }

  private onGuaranteeCreated(): void {
    const guaranteed = this.line()?.decision?.answer === GuaranteeDecision.Guaranteed;
    const resolution = this.translate.instant(guaranteed ? 'task.message.serviceRequestGuaranteed' : 'task.message.serviceRequestNotGuaranteed');
    this.store.dispatch(TaskActions.resolveTask({ task: this.task, payload: { resolution } }));
    this.router.navigate(['/tasks']);
  }

  protected openCoverage(userId: string): void {
    this.dialog.open<MemberCoverageDialogComponent, MemberCoverageDialogData>(MemberCoverageDialogComponent, { ...MEMBER_COVERAGE_DIALOG_CONFIG, data: { userId } });
  }

  protected navigateToMemberProfile(): void {
    this.router.navigate(['/members', this.task.data.memberId]);
  }
}
